import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { UploadedFile } from '../../firestore-models/uploaded-file';
import { ApiUploadedFile, ApiSetUploadedFilesRequest } from '../../api-client-module';

import { ApiClientService } from '../../api-client.service';
import { ConfigurationService } from '../../configuration.service';
import { SpinnerService } from '../../services/spinner.service';
import { AppConst } from '../../app-const';
import { MessageDialogComponent } from '../../common/message-dialog/message-dialog.component';

@Component({
  selector: 'app-uploader-dl',
  templateUrl: './uploader-dl.component.html',
  styleUrls: ['./uploader-dl.component.css'],
  standalone: false,
})
export class UploaderDLComponent implements OnInit, OnDestroy {

  private uploadedFilesCollection!: AngularFirestoreCollection<UploadedFile>;
  uploadedFilesObservable!: Observable<UploadedFile[]>;
  uploadedFilesSubscribe!: Subscription;
  projectId: string;

  // マウスホバー時の変化
  isHovering: boolean = false;
  // プロジェクトに登録される画像ファイル
  files: File[] = [];
  compless: string = "1";

  displayedColumns: string[] = ['position', 'select', 'file', 'name', 'date', 'download'];
  dataSource = new MatTableDataSource<UploadedFile>();
  selection = new SelectionModel<UploadedFile>(true, []);

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService
  ) {
    this.projectId = this.configurationService.getProjectId();
    }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.uploadedFilesCollection = this.angularFirestore.collection<UploadedFile>(
      AppConst.FIRESTORE_UPLOADED_FILES, ref => ref.where('project_id', '==', this.projectId).where('type', '==', 'DL').orderBy('created', 'desc'));
    this.uploadedFilesObservable = this.uploadedFilesCollection.valueChanges({ idField: 'id' });
    this.uploadedFilesSubscribe = this.uploadedFilesObservable.subscribe(uploadedFiles => {
      this.dataSource.data = uploadedFiles;
    });
  }

  ngOnDestroy(): void {
    this.uploadedFilesSubscribe.unsubscribe();
  }

  //全選択
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  //チェックボックス
  checkboxLabel(row?: UploadedFile): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //ホバー時のイベント発火
  toggleHover(event: boolean) {
    this.isHovering = event;
  }

  //ファイルアップロード（ドラッグ&ドロップ）
  onDrop(files: any) {
    for (let i = 0; i < files.length; i++) {
      this.files.push(files.item(i)!);
    }
  }

  //ファイルアップロード（ファイル選択）
  onChangeFileInput(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files!.length > 0) {
      for (let i = 0; i < target.files!.length; i++) {
        this.files.push(target.files!.item(i)!);
      }
    }
  }

  //ファイルの消去
  deleteData(): void{
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: '選択されたデータを削除します。よろしいですか？' }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedFiles: Array<ApiUploadedFile> = new Array<ApiUploadedFile>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedFile: ApiUploadedFile = {
            id: row.id,
            type: row.type ? row.type : '',
            file_name: row.file_name,
            file_path: row.file_path,
            download_url: row.download_url,
            project_id: row.project_id,
            enabled: row.enabled
          };
          selectedFiles.push(selectedFile);
        }
      }
      if (!!result) {
        let request: ApiSetUploadedFilesRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          uploaded_files: selectedFiles
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setUploadedFiles(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success('削除しました。');
            } else {
              this.toastr.error('削除できませんでした。他で利用中のデータが含まれている可能性があります。');
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error('削除できませんでした。');
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  isImage(uploadedFile: UploadedFile): boolean {
    // 拡張子取得
    const pos = uploadedFile.file_name.lastIndexOf('.');
    let ext = '';
    if (pos !== -1) {
      ext = uploadedFile.file_name.slice(pos + 1).toLowerCase();
    }
    if (AppConst.IMAGE_FILE_EXTS.indexOf(ext) === -1) {
      return false;
    }
    return true
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'name':
          return compare(a.file_name.substr(14), b.file_name.substr(14), isAsc);
        case 'date':
          return compare(a.created.toString(), b.created.toString(), isAsc);
        default:
          return 0;
      }
    });
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  complessChange(event: any) {
    this.compless = event.value;
  }

  downloadFile(uploadedFile: UploadedFile): void {
    fetch(uploadedFile.download_url)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = uploadedFile.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Download failed:', error);
      });
  }

}
